import React, {useState} from 'react';
import {useAuth} from '@/contexts/AuthContext.tsx';
import Modal from './ui/Modal';
import {useAccountUpgrade} from '../hooks/useAccountUpgrade';

interface AccountUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountUpgradeModal: React.FC<AccountUpgradeModalProps> = ({isOpen, onClose}) => {
    const {user} = useAuth();
  const { 
    upgradeAccount, 
    isLoading, 
    error, 
    success, 
    resetState 
  } = useAccountUpgrade();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [formError, setFormError] = useState('');

  const handleUpgradeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    if (!email || !password || !confirmPassword) {
      setFormError('All fields are required');
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    const upgradeSuccess = await upgradeAccount({
      email,
      password,
      displayName: displayName.trim() || undefined
    });

    // Close modal after successful upgrade
    if (upgradeSuccess) {
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  };

  const handleClose = () => {
    resetState();
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName(user?.displayName || '');
    setFormError('');
    onClose();
  };

  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Account Upgraded!" maxWidth="md">
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
            <span className="text-green-500 text-2xl">✓</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Account Upgraded Successfully!</h3>
          <p className="text-gray-500 mb-4">Your account has been upgraded to a permanent account.</p>
          <p className="text-sm text-gray-400">This window will close automatically...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upgrade Your Account" maxWidth="2xl">
      <div className="space-y-6">
        {/* Benefits Overview */}
        <div className="mb-6">
          <p className="text-gray-600 mb-4">Convert your guest account to a permanent account</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Benefits of upgrading:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Permanent data storage</li>
              <li>• Sync across devices</li>
              <li>• Access to premium features</li>
              <li>• Priority support</li>
            </ul>
          </div>
        </div>
        
        {/* Upgrade Form */}
        <div className="border-t border-gray-200 pt-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Create Your Permanent Account
          </h3>
          
          <form onSubmit={handleUpgradeSubmit} className="space-y-4">
            {(error || formError) && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-red-400 text-xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">
                      {formError || error}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="your.email@example.com"
                required
              />
            </div>
            
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name / Display Name (Optional)
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Your name"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Create a secure password"
                minLength={6}
                required
              />
              <p className="text-xs text-gray-500 mt-1">Password must be at least 6 characters</p>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
                required
              />
            </div>
            
            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Upgrading Account...' : 'Upgrade Account Now'}
              </button>
            </div>
            
            <p className="text-center text-sm text-gray-500">
              All your existing data will be preserved
            </p>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AccountUpgradeModal; 